<script lang="ts" setup>
import type { FormInstance, FormRules } from 'element-plus'
import { reactive, ref } from 'vue'

import { useUpdateCampaignParticipant } from '@/api/campaignParticipant.api'
import type { CampaignParticipantDetailsType } from '@/types/campaignParticipant.types'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const emits = defineEmits(['onCancel', 'onConfirm'])
const props = defineProps<{ participant: CampaignParticipantDetailsType; campaignId: number }>()
const { isLoading, addComment } = useUpdateCampaignParticipant(props.campaignId)

const isOpen = defineModel<boolean>('modelValue', { default: false })
const rejectionReason = ref('')

const formRef = ref<FormInstance>()
const formData = reactive({ reason: '' })
const rules = reactive<FormRules<{ reason: string }>>({
  reason: [
    { max: 1500, message: 'Field must be shorter than 15000 symbols', trigger: 'change' },
    { required: true, message: 'This field is required', trigger: 'change' },
  ],
})

const handleCancel = () => {
  formData.reason = ''
  emits('onCancel')
}

const handleConfirm = async (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }
  await formEl.validate(async (valid) => {
    if (valid) {
      await addComment(props.participant.id, formData.reason)
      formData.reason = ''
      isOpen.value = false
      emits('onConfirm', rejectionReason.value)
    }
  })
}
</script>

<template>
  <el-dialog
    v-model="isOpen"
    :width="480"
    :show-close="false"
    class="reject-dialog"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
  >
    <BoxComponent :mb="16">
      <TypographyComponent variant="headline-medium"> Reject creator </TypographyComponent>
    </BoxComponent>
    <el-form ref="formRef" class="w-100" :model="formData" :rules="rules" @submit.prevent="handleConfirm(formRef)">
      <BoxComponent :mb="24">
        <TypographyComponent variant="body-large" color="#222"> Tell us why you want to reject this creator </TypographyComponent>
      </BoxComponent>
      <el-form-item prop="reason">
        <el-input v-model="formData.reason" type="textarea" :rows="4" class="reject-reason" />
      </el-form-item>
      <BoxComponent :mt="16">
        <FlexComponent justify="flex-end" :gap="8">
          <el-button plain round class="cancel-btn" @click="handleCancel">Cancel</el-button>
          <ButtonComponent variant="accent" :is-disabled="!formData.reason || isLoading" type="submit"> Confirm </ButtonComponent>
        </FlexComponent>
      </BoxComponent>
    </el-form>
  </el-dialog>
</template>

<style lang="scss" scoped>
:deep(.el-textarea__inner) {
  resize: none;
  border-radius: 8px;
  padding: 16px;
}

.cancel-btn {
  border-color: transparent;
}
</style>
